import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex items-center flex-col md:flex-row gap-3 md:gap-5 mt-7" }
const _hoisted_2 = { class: "flex gap-3 md:gap-5 items-center" }
const _hoisted_3 = { class: "flex justify-end mt-2.5" }
const _hoisted_4 = {
  key: 1,
  class: "max-w-[370px] mx-auto mt-[86px]"
}
const _hoisted_5 = { class: "flex flex-col gap-6" }
const _hoisted_6 = { class: "text-info-900" }
const _hoisted_7 = { class: "text-info-900" }

import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import Axios from '~src/shared/axios';
import { AppConfigType, FiltersType, PostType } from '../types';
import Button from '../components/base/Button.vue';
import TextInput from '../components/form/TextInput.vue';
import ActiveFilters from '../components/filters/ActiveFilters.vue';
import Keywords from '../components/filters/Keywords.vue';
import Status from '../components/filters/Status.vue';
import Card from '../components/layout/Card.vue';
import Container from '../components/layout/Container.vue';
import PageHeading from '../components/layout/PageHeading.vue';
import Pagination from '../components/posts/Pagination.vue';
import PostsTable from '../components/posts/PostsTable.vue';
import { useNotificationStore } from '~src/admin/stores/useNotificationStore';
import useSetPageTitle from '../composables/useSetPageTitle';
import { useGettext } from 'vue3-gettext';

// Translations

export default /*@__PURE__*/_defineComponent({
  __name: 'ListOfArticles',
  setup(__props) {

const { $gettext } = useGettext();

// Set page title
useSetPageTitle($gettext('List of articles'));

// Get plugin config
const config: AppConfigType = inject('pluginConfig');

// Router
const router = useRouter();

// Show notification
const { addNotification } = useNotificationStore();

// Filter component
const statusComponent = ref<InstanceType<typeof Status>>();
const keywordsComponent = ref<InstanceType<typeof Keywords>>();

// Active filters
const activeFilters = ref<FiltersType>({
    search: '',
    status: [],
    keywords: [],
});

// Status map
const statusMap = {
    'Scheduled to be generated': 'schedule_generate',
    'Scheduled for publication': 'schedule_publish',
    Published: 'published',
};

// Pagination
const currentPage = ref(1);
const pageSize = ref(12);
const totalPosts = ref(0);

// Loading
const loading = ref(false);

// Posts to be displayed
const posts = ref<PostType[]>([]);

// AbortController for cancelling requests
let abortController: AbortController | null = null;

// Get posts on mounted
onMounted(() => {
    fetchPosts();
});

// Change current page when page size changes
watch(pageSize, () => {
    currentPage.value = 1;
});

// Get posts when pagination or filters change
watch(
    [currentPage, pageSize, activeFilters],
    () => {
        fetchPosts();
    },
    {
        deep: true,
    }
);

// Get posts function
const fetchPosts = async () => {
    try {
        // Cancel the previous request if it exists
        if (abortController) {
            abortController.abort();
        }

        // Create a new AbortController instance
        abortController = new AbortController();

        // Set loading and reset posts
        posts.value = [];
        loading.value = true;

        // Construct the URL with query parameters
        const url = new URL(config.rest.endpoints.posts);
        const params = new URLSearchParams();

        // Add pagination parameters
        params.append('per_page', pageSize.value.toString());
        params.append('page', currentPage.value.toString());

        // Add the search parameter if it exists
        if (activeFilters.value.search) {
            params.append('search', activeFilters.value.search);
        }

        // Add the status parameter if it exists
        if (activeFilters.value.status?.length > 0) {
            params.append('status', activeFilters.value.status.map((status) => statusMap[status]).join(','));
        }

		 // Add the keywords parameter if it exists
		 if (activeFilters.value.keywords?.length > 0) {
            params.append('keywords', activeFilters.value.keywords.join(','));
        }

        // Append the query parameters to the URL
        url.search = params.toString();

        // Call the REST API
        const response = await Axios.get(url.toString(), { signal: abortController.signal });

        // Check if the response is successful
        if (response.status !== 200 && !response.data.success) {
            throw new Error(`REST API error: ${response.status} - ${response.statusText}`);
        }

        // Save the posts
        posts.value = response.data.data.posts;

        // Save pagination data
        currentPage.value = response.data.data.current_page;
        totalPosts.value = response.data.data.total_posts;

        loading.value = false;
    } catch (error) {
        // Handle error
        if (error.name !== 'CanceledError') {
            console.error($gettext('Error fetching posts:'), error);

            addNotification({
                message: $gettext('There was an error while fetching the posts!'),
                type: 'danger',
            });
            loading.value = false;
        }
    }
};

// Check if any filters are active
const filtersActive = computed(() => {
    return activeFilters.value.search || activeFilters.value.status.length || activeFilters.value.keywords.length;
});

// Account type
const isAccountFree = inject<boolean>('isAccountFree');

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Container, null, {
    default: _withCtx(() => [
      _createVNode(PageHeading, {
        heading: _unref($gettext)('List of articles')
      }, null, 8, ["heading"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(TextInput, {
          class: "max-w-[300px]",
          placeholder: _unref($gettext)('Search...'),
          modelValue: activeFilters.value.search,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeFilters.value.search) = $event)),
          "is-search": true
        }, null, 8, ["placeholder", "modelValue"]),
        _createElementVNode("p", null, _toDisplayString(_unref($gettext)('or filter by')), 1),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(Status, {
            ref_key: "statusComponent",
            ref: statusComponent,
            onUpdateActiveStatus: _cache[1] || (_cache[1] = (activeStatus) => activeFilters.value.status = activeStatus)
          }, null, 512),
          _createVNode(Keywords, {
            ref_key: "keywordsComponent",
            ref: keywordsComponent,
            onUpdateSelectedKeywords: _cache[2] || (_cache[2] = (selectedKeywords) => activeFilters.value.keywords = selectedKeywords)
          }, null, 512)
        ])
      ]),
      (filtersActive.value)
        ? (_openBlock(), _createBlock(ActiveFilters, {
            key: 0,
            "active-filters": activeFilters.value,
            onUpdateStatus: _cache[3] || (_cache[3] = (status) => statusComponent.value.updateStatus(status)),
            onUpdateKeywords: _cache[4] || (_cache[4] = (keyword) => keywordsComponent.value.removeKeyword(keyword))
          }, null, 8, ["active-filters"]))
        : _createCommentVNode("", true),
      _createVNode(PostsTable, {
        class: "mt-5",
        posts: posts.value,
        type: "full",
        loading: loading.value,
        onReloadPosts: _cache[5] || (_cache[5] = ($event: any) => (fetchPosts()))
      }, null, 8, ["posts", "loading"]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(Pagination, {
          totalPosts: totalPosts.value,
          "current-page": currentPage.value,
          "onUpdate:currentPage": _cache[6] || (_cache[6] = ($event: any) => ((currentPage).value = $event)),
          "page-size": pageSize.value,
          "onUpdate:pageSize": _cache[7] || (_cache[7] = ($event: any) => ((pageSize).value = $event))
        }, null, 8, ["totalPosts", "current-page", "page-size"])
      ]),
      (_unref(isAccountFree))
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(Card, {
              "image-url": `${_unref(config).plugin_url}/public/images/articles.png`,
              "image-alt": _unref($gettext)('Upgrade to premium plan'),
              type: "vertical"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("p", _hoisted_6, _toDisplayString(_unref($gettext)('On trial you can generate and publish only one article.')), 1),
                  _createElementVNode("p", _hoisted_7, _toDisplayString(_unref($gettext)(
                                'Upgrade to paid plan to publish articles more often - few times a day, on different hours.'
                            )), 1),
                  _createVNode(Button, {
                    text: _unref($gettext)('Upgrade'),
                    size: "large",
                    onClick: _cache[8] || (_cache[8] = _withModifiers(($event: any) => (_unref(router).push('/upgrade')), ["prevent"]))
                  }, null, 8, ["text"])
                ])
              ]),
              _: 1
            }, 8, ["image-url", "image-alt"])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})